/**
 * Amazon India static rate cards — copied verbatim from Amazon's official
 * sellercentral pages. Update when Amazon revises the rates (~1-2 times per year).
 *
 * Sources:
 *   • Closing Fee:  https://sellercentral.amazon.in/help/hub/reference/200336920
 *                   — "Fulfilment center (excluding Seller Flex)" — effective March 16, 2026
 *   • Easy Ship:    Easy Ship Weight Handling Fee — "INR per shipment, based on
 *                   outbound shipping weight" — effective March 16, 2026
 *
 * If Amazon publishes a new rate card, replace these arrays and bump LAST_UPDATED.
 */

export const RATE_CARDS_LAST_UPDATED = '2026-03-16';

// ──────────────────────────────────────────────────────────────────────────────
// GST on Amazon service fees
// ──────────────────────────────────────────────────────────────────────────────
// Amazon charges 18% GST on top of every service fee they deduct (referral,
// closing, FBA, Easy Ship). The published rate-card values are GST-EXCLUSIVE
// base amounts — gross them up before showing the actual cash deduction.
//
// Note: this GST is recoverable by the seller as Input Tax Credit (ITC) in
// their GSTR-3B filing. So it hits cash flow in the short term but is not a
// final cost — only a deferred recovery.

export const AMAZON_SERVICE_GST_PCT = 18;

/**
 * Add 18% GST to a base fee. Returns gross-with-GST amount.
 * Use this for any rate-card-derived fee (closing, Easy Ship) — historical
 * averages and SP-API responses already include GST and should NOT be
 * grossed up again.
 */
export function withServiceGst(base) {
  const b = Number(base) || 0;
  return b * (1 + AMAZON_SERVICE_GST_PCT / 100);
}

/** Returns just the GST portion (for display when splitting base + GST). */
export function serviceGstOf(base) {
  const b = Number(base) || 0;
  return b * (AMAZON_SERVICE_GST_PCT / 100);
}

// ──────────────────────────────────────────────────────────────────────────────
// CLOSING FEE — flat ₹ per item, varies by (item_price_band × category_group)
// "Item price including shipping charges" is the lookup key.
// ──────────────────────────────────────────────────────────────────────────────

export const CLOSING_FEE_GROUPS = [
  {
    key: 'STANDARD',
    label: 'Standard',
    description: 'Default for most categories — applies unless your category is on the Select list.',
  },
  {
    key: 'SELECT_A',
    label: 'Select cat. (Group A)',
    description: 'Reduced fees for select categories — Group A tier.',
  },
  {
    key: 'SELECT_B',
    label: 'Select cat. (Group B)',
    description: 'Reduced fees for select categories — Group B tier (lowest).',
  },
];

export const CLOSING_FEE_TABLE = [
  // priceMin (inclusive), priceMax (exclusive null = +∞), fee per group
  { priceMin: 0,    priceMax: 301,  STANDARD: 26, SELECT_A: 20, SELECT_B: 13 },
  { priceMin: 301,  priceMax: 501,  STANDARD: 22, SELECT_A: 18, SELECT_B: 14 },
  { priceMin: 501,  priceMax: 1001, STANDARD: 27, SELECT_A: 27, SELECT_B: 27 },
  // Above ₹1000: ₹52 standard / ₹72 oversized — using ₹52 as default (oversized is rare)
  { priceMin: 1001, priceMax: null, STANDARD: 52, SELECT_A: 52, SELECT_B: 52 },
];

/**
 * Look up the closing fee for a given price + category group.
 * @param {number} price - "item price including shipping" in INR
 * @param {string} group - one of STANDARD | SELECT_A | SELECT_B
 * @returns {number} fee in INR, or 0 if not found
 */
export function lookupClosingFee(price, group = 'STANDARD') {
  const p = Number(price) || 0;
  const g = group || 'STANDARD';
  const row = CLOSING_FEE_TABLE.find(
    (r) => p >= r.priceMin && (r.priceMax == null || p < r.priceMax)
  );
  if (!row) return 0;
  return row[g] ?? row.STANDARD ?? 0;
}

/** Human-readable description of which band a price falls into. */
export function describeClosingFeeBand(price) {
  const p = Number(price) || 0;
  if (p <= 0) return null;
  if (p < 301) return '₹0–300';
  if (p < 501) return '₹301–500';
  if (p < 1001) return '₹501–1,000';
  return 'Above ₹1,000';
}

// ──────────────────────────────────────────────────────────────────────────────
// EASY SHIP WEIGHT HANDLING FEE — flat ₹ per shipment
// Lookup keys: weight (in grams) × STEP level
// "STEP" = Service Excellence Program tier the seller is in.
// ──────────────────────────────────────────────────────────────────────────────

export const EASY_SHIP_STEP_LEVELS = [
  { key: 'PREMIUM_ADVANCED', label: 'Premium / Advanced', tone: 'emerald' },
  { key: 'STANDARD',         label: 'Standard',           tone: 'sky' },
  { key: 'BASIC',            label: 'Basic',              tone: 'slate' },
];

/**
 * Per-slab base fees + per-kg additional rates beyond 2kg.
 * Each row: weight ≤ maxGrams → flat fee. After 2kg, fees compound by kg.
 */
export const EASY_SHIP_BASE = {
  // First 500 g
  PREMIUM_ADVANCED: { firstHalfKg: 53, halfToOneKg: 73,  oneToTwoKg: 110, perKgAfter2: 34, perKgAfter5: 18 },
  STANDARD:         { firstHalfKg: 55, halfToOneKg: 75,  oneToTwoKg: 112, perKgAfter2: 34, perKgAfter5: 18 },
  BASIC:            { firstHalfKg: 59, halfToOneKg: 79,  oneToTwoKg: 116, perKgAfter2: 34, perKgAfter5: 18 },
};

/**
 * Compute Easy Ship Weight Handling Fee (flat ₹) for a shipment.
 *
 * Slabs (per Amazon):
 *   ≤ 500 g       → firstHalfKg
 *   500 g – 1 kg  → halfToOneKg
 *   1 kg – 2 kg   → oneToTwoKg
 *   each addl kg up to 5 kg  → +perKgAfter2
 *   each addl kg beyond 5 kg → +perKgAfter5
 *
 * Math example: 3.4 kg, Standard → 112 (up to 2kg) + 2 × 34 (kg 3 & 4) = 112 + 68 = 180
 * (we round UP to next full kg above 2kg, matching Amazon's billing behaviour)
 *
 * @param {number} grams - outbound shipping weight in grams
 * @param {string} stepLevel - PREMIUM_ADVANCED | STANDARD | BASIC
 * @returns {number} fee in INR
 */
export function lookupEasyShipFee(grams, stepLevel = 'STANDARD') {
  const g = Number(grams) || 0;
  if (g <= 0) return 0;
  const r = EASY_SHIP_BASE[stepLevel] || EASY_SHIP_BASE.STANDARD;

  if (g <= 500) return r.firstHalfKg;
  if (g <= 1000) return r.halfToOneKg;
  if (g <= 2000) return r.oneToTwoKg;

  // Above 2 kg: base for 1-2 kg plus per-kg increments
  let fee = r.oneToTwoKg;
  // Number of additional kg above 2 (rounded up)
  const totalKg = Math.ceil(g / 1000);
  const addlKgUpTo5 = Math.max(0, Math.min(totalKg, 5) - 2); // kg beyond 2 up to 5
  const addlKgBeyond5 = Math.max(0, totalKg - 5);
  fee += addlKgUpTo5 * r.perKgAfter2;
  fee += addlKgBeyond5 * r.perKgAfter5;
  return fee;
}

/** Human-readable description of which weight band a shipment falls into. */
export function describeEasyShipBand(grams) {
  const g = Number(grams) || 0;
  if (g <= 0) return null;
  if (g <= 500) return 'First 500 g';
  if (g <= 1000) return '500 g – 1 kg';
  if (g <= 2000) return '1 kg – 2 kg';
  const kg = Math.ceil(g / 1000);
  return `${kg} kg`;
}
